import { PlusIcon, SearchIcon, SettingsIcon } from "lucide-react";
import { getShortcut } from "@/config/shortcuts";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useSidebar } from "@/components/ui/sidebar";
import { CommandShortcut } from "@/components/ui/command";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useMobile } from "@/hooks/useMobile";

interface AppNavbarProps {
  onSearchClick?: () => void;
  onNewChatClick?: () => void;
  onSettingsClick?: () => void;
}

export function AppNavbar({ onSearchClick, onNewChatClick, onSettingsClick }: AppNavbarProps) {
  const { state } = useSidebar();
  const isMobile = useMobile();

  return (
    <>
      {/* Left section */}
      <div className="fixed top-2 md:top-0 bg-transparent lg:bg-sidebar lg:dark:bg-black left-0 z-20 flex items-center gap-2 p-3 h-[57px]">
        <SidebarTrigger className="bg-sidebar-accent hover:bg-sidebar-accent/80 active:bg-sidebar-accent/90 transition-colors size-11 md:size-10" />
        <div
          className={cn(
            "flex items-center gap-2 transition-all duration-100",
            state === "collapsed" || isMobile
              ? "opacity-100 translate-x-0"
              : "hidden opacity-0 -translate-x-4 pointer-events-none",
          )}
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="secondary"
                size="icon"
                className="bg-sidebar-accent hover:bg-sidebar-accent/80 active:bg-sidebar-accent/90 transition-colors size-11 md:size-10"
                onClick={onSearchClick}
              >
                <SearchIcon size={24} />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <div className="flex items-center gap-2">
                <span>Search</span>
                <CommandShortcut>{getShortcut("openCommandMenu").display}</CommandShortcut>
              </div>
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="secondary"
                size="icon"
                className="bg-sidebar-accent hover:bg-sidebar-accent/80 active:bg-sidebar-accent/90 transition-colors size-11 md:size-10"
                onClick={onNewChatClick}
              >
                <PlusIcon size={24} />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <div className="flex items-center gap-2">
                <span>New Chat</span>
                <CommandShortcut>{getShortcut("newChat").display}</CommandShortcut>
              </div>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>

      {/* Right section */}
      <div className="fixed top-2 md:top-0 right-0 z-20 flex items-center gap-2 p-3 h-[57px]">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="secondary"
              size="icon"
              className="bg-sidebar-accent hover:bg-sidebar-accent/80 active:bg-sidebar-accent/90 transition-colors size-11 md:size-10"
              onClick={onSettingsClick}
            >
              <SettingsIcon size={24} />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <div className="flex items-center gap-2">
              <span>Settings</span>
              <CommandShortcut>{getShortcut("settings").display}</CommandShortcut>
            </div>
          </TooltipContent>
        </Tooltip>
        <ThemeToggle />
      </div>
    </>
  );
}
